import endpoints from '@app/src/api/endpoints'
import { useCreateResource } from '@app/src/api/updateHooks'
import Select from '@app/src/components/Form/Select/ControlledSelect'
import LoadingButton from '@app/src/components/LoadingButton'
import { useSnackbar } from '@app/src/context/SnackbarContext'
import useErrorNotification from '@app/src/hooks/errorNotification'
import type { QuestionCategoryApi, QuestionCategoryOption, QuestionCategoryType } from '@app/src/types/resourceExplorer'
import { NotificationSeverity } from '@app/src/wf-constants'
import { Grid, TextField, Typography } from '@mui/material'
import React from 'react'
import { useForm } from 'react-hook-form'
import { useIntl } from 'react-intl'
import { categoryTypeOrder, useQuestionCategories } from '@app/src/hooks/useQuestionCategories'

interface LibraryTagFormData {
  categoryId?: number
  name: string
}

const AddLibraryTag: React.FC = () => {
  const { control, register, handleSubmit, watch, setValue } = useForm<LibraryTagFormData>()
  const { formatMessage } = useIntl()
  const { mutateAsync: createTagAsync, isPending: isCreatingTag } = useCreateResource<
    QuestionCategoryOption,
    LibraryTagFormData
  >()
  const { showSnackbar } = useSnackbar()
  const { showErrorNotification } = useErrorNotification()

  const categoryId = watch('categoryId')
  const categoryOptionName = watch('name')

  const allFieldsFilled = Boolean(categoryId) && categoryOptionName?.length > 0

  const { categories } = useQuestionCategories()
  const options = categories.map(category => ({
    label: category.name,
    value: category.id,
    additionalText: category.categoryType,
  }))

  const onSubmit = async (formData: LibraryTagFormData) => {
    await createTagAsync(
      { url: endpoints.questionCategoryOption, body: formData },
      {
        onSuccess: () => {
          showSnackbar({ message: 'The tag was created', severity: NotificationSeverity.success })
          setValue('name', '')
        },
        onError: error => {
          if (error.statusCode === 400 || error.statusCode === 409)
            showSnackbar({ message: error.body?.message, severity: NotificationSeverity.error })
          else showErrorNotification({ requestError: error })
        },
      },
    )
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Typography variant="subtitle1" mb={3}>
        Add library tag
      </Typography>
      <Grid container spacing={1}>
        <Grid item md={4} xs={12}>
          <Select<number, QuestionCategoryApi>
            control={control}
            variant="outlined"
            name="categoryId"
            fieldLabel="Category to add tag to"
            findSelectedValue={(value, option) => value === option.value}
            groupBy={option => `${option.additionalText}`}
            options={options}
            customSort={(a, b) => {
              const aIndex = categoryTypeOrder.indexOf(a.additionalText as QuestionCategoryType)
              const bIndex = categoryTypeOrder.indexOf(b.additionalText as QuestionCategoryType)
              return aIndex - bIndex
            }}
          />
        </Grid>
        <Grid item md={4} xs={12}>
          <TextField
            variant="outlined"
            sx={{ width: '100%' }}
            inputRef={register()}
            label="Tag name"
            name="name"
            inputProps={{ maxLength: 60 }}
          />
        </Grid>
        <Grid item md={4} xs={12}>
          <LoadingButton
            variant="contained"
            type="submit"
            disabled={!allFieldsFilled}
            loading={isCreatingTag}
            sx={{ height: '100%', width: '50%' }}
          >
            {formatMessage({ id: 'general.save' })}
          </LoadingButton>
        </Grid>
      </Grid>
    </form>
  )
}

export default AddLibraryTag
